import moment from 'moment'
import _ from 'lodash'

import React, { useEffect, useState } from 'react'
import { compose } from 'recompose'
import { ConnectedProps, connect } from 'react-redux'
import { change, getFormValues, isDirty as isDirtyForm } from 'redux-form'

import { Signature } from 'services/medicationForms/models'
import { UPLOAD_DIRECTORY } from 'services/legacy/upload/constants'
import { FREQUENCIES, FREQUENCY_LABELS, MF_SIGNATURE_TYPES } from 'services/medicationForms/constants'
import { Option } from 'components/InfiniteDropdowns/InfiniteDropdownsModel'
import { ModalType } from 'modals'
import { Administration } from 'services/administrations/models'

import { withModalService, withModalServiceProps } from 'services/utils/modal'
import { withAdministrationsService, withAdministrationsServiceProps } from 'services/administrations'
import { withUploadService, withUploadServiceProps } from 'services/legacy/upload'

import { TimeString } from 'components'

import i18n from 'translations'

import AddAdministrationView from './AddAdministrationModalView'
import { ADD_ADMINISTRATION_FORM, AddAdministrationFormValues } from './components/AddAdministrationModalForm'

const ADMINISTRATIONS_GROUPS = {
  read: [
    'administration.medicationForm',
    'medicationForm',
    'medicationForm.medicationDetails',
    'medicationForm:medicationDetails',
    'medicationDetails',
    'medicationForm.child',
  ],
}

export interface AddAdministrationModalProps {
  onSubmitSuccess?: () => void
  params?: {
    childOption?: Option
    doseOption?: Option
    medicineOption?: Option
  }
}

export type AddAdministrationModalContainerProps = AddAdministrationModalProps
  & withModalServiceProps
  & withAdministrationsServiceProps
  & withUploadServiceProps

const mapState = (state) => ({
  formValues: getFormValues(ADD_ADMINISTRATION_FORM)(state) as AddAdministrationFormValues,
  isDirty: isDirtyForm(ADD_ADMINISTRATION_FORM)(state),
})

const mapDispatch = {
  changeValue: (field, value) => change(ADD_ADMINISTRATION_FORM, field, value),
}

const connector = connect(mapState, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

const AddAdministrationModalContainer: React.FC<AddAdministrationModalContainerProps & PropsFromRedux> = ({
  administrationsActions,
  administrationsSelectors,
  changeValue,
  formValues,
  isDirty,
  modalActions,
  onSubmitSuccess,
  params,
  uploadActions,
}) => {
  const [administrationData, setAdministrationData] = useState<Administration[]>()
  const [medicineOptions, setMedicineOptions] = useState<Option[]>()
  const [doseOptions, setDoseOptions] = useState<Option[]>()
  const [isInitialize, setIsInitialized] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<AddAdministrationFormValues>()

  const isChildContext = !!params?.childOption

  const { child, medicine } = formValues || {}

  const childValue = child?.value

  useEffect(() => {
    if (childValue) {
      const criteria = administrationsSelectors.getCriteria({
        administrationDateRange: [moment(), moment()],
        childId: childValue as number,
        pendingOnly: true,
      })

      administrationsActions.list({
        onSuccess: ({ data }) => setAdministrationData(data),
        onlyData: true,
        params: {
          criteria,
          groups: ADMINISTRATIONS_GROUPS,
        },
      })
    }

    if (!isChildContext && isInitialize) {
      changeValue('medicine', null)
      changeValue('dose', null)
    }

    if (!isInitialize) {
      setIsInitialized(true)
    }
  }, [childValue])

  useEffect(() => {
    setMedicineOptions(null)
    setDoseOptions(null)

    if (administrationData?.length) {
      const uniqueData = _.uniqBy(
        _.map(administrationData, ({ medicationForm }) => medicationForm),
        'id',
      )

      setMedicineOptions(_.map(uniqueData, (item) => {
        const { id: medicationFormId, medicationDetails } = item
        const { medication } = medicationDetails

        return {
          label: medication,
          value: medicationFormId,
        }
      }))
    }
  }, [administrationData])

  useEffect(() => {
    const formInitialValues = {
      child: null,
      dose: null,
      medicine: null,
      timeOfAdministration: moment(),
    }

    if (params?.childOption) {
      formInitialValues.child = params.childOption
    }

    if (params?.medicineOption) {
      formInitialValues.medicine = params.medicineOption
    }

    if (params?.doseOption) {
      formInitialValues.dose = params.doseOption
    }

    setInitialValues(formInitialValues)
  }, [params, medicine])

  useEffect(() => {
    if (medicine) {
      const filteredData = _.filter(administrationData, ({ medicationForm }) => (
        medicationForm.id === medicine?.value
      ))

      const medicationDoseOptions = _.map(filteredData, (item) => {
        const { id, medicationForm, takenAt, timeOfAdministration } = item
        const { medicationDetails } = medicationForm
        const { dose, frequency } = medicationDetails

        return {
          label: (
            <React.Fragment>
              {frequency === FREQUENCIES.AS_NEEDED
                ? FREQUENCY_LABELS[FREQUENCIES.AS_NEEDED]
                : <TimeString date={(takenAt || timeOfAdministration) as Date} local={false} />}
              {', '}
              {dose}
            </React.Fragment>
          ),
          value: id,
        }
      })

      setDoseOptions(medicationDoseOptions)

      if (1 === medicationDoseOptions?.length) {
        changeValue('dose', medicationDoseOptions[0])
      }
    } else if (isInitialize) {
      changeValue('dose', null)
      setDoseOptions(null)
    }
  }, [medicine, administrationData])

  const handleCancelClick = (isSaved?: boolean) => {
    if (isDirty && !isSaved) {
      modalActions.show<ModalType.CONFIRM>(ModalType.CONFIRM, {
        cancelButtonLabel: i18n.t('global:Continue'),
        confirmButtonLabel: i18n.t('global:Discard'),
        icon: 'warning',
        onConfirm: () => modalActions.hide(),
        text: i18n.t('module:Safeguarding:MedicationForm:Add:leavePopupMessage'),
      })

      return
    }

    modalActions.hide()
  }

  const handleSubmit = async (fields: AddAdministrationFormValues) => {
    setIsSubmitting(true)

    const {
      administerSignature,
      administratedBy,
      dose,
      witness,
      witnessSignature,
    } = fields

    const signatureBody: Signature[] = []

    signatureBody.push({
      owner: {
        id: administratedBy.value as number,
      },
      signature: {
        mimeType: administerSignature?.file?.type,
        size: administerSignature?.file?.size,
        url: await uploadActions.uploadFile(
          administerSignature?.file,
          administerSignature?.file.name,
          UPLOAD_DIRECTORY.IMAGES,
        ) as any,
      },
      type: MF_SIGNATURE_TYPES.STAFF,
    })

    signatureBody.push({
      owner: {
        id: witness.value as number,
      },
      signature: {
        mimeType: witnessSignature?.file?.type,
        size: witnessSignature?.file?.size,
        url: await uploadActions.uploadFile(
          witnessSignature?.file,
          witnessSignature?.file.name,
          UPLOAD_DIRECTORY.IMAGES,
        ) as any,
      },
      type: MF_SIGNATURE_TYPES.WITNESS,
    })

    const body = administrationsSelectors.getBody(fields)

    administrationsActions.update({
      body: {
        ...body,
        signatures: signatureBody?.length ? signatureBody : undefined,
      },
      onFailed: () => {
        setIsSubmitting(false)
      },
      onSuccess: () => {
        onSubmitSuccess()
        setIsSubmitting(false)
        handleCancelClick(true)
      },
      params: [dose.value, {}],
    })
  }

  return (
    <AddAdministrationView
      doseOptions={doseOptions}
      formValues={formValues}
      initialValues={initialValues}
      isChildContext={isChildContext}
      isSubmitting={isSubmitting}
      medicineOptions={medicineOptions}
      onCancelClick={() => handleCancelClick()}
      onSubmit={handleSubmit}
    />
  )
}

const enhance = compose(
  withModalService,
  withAdministrationsService,
  withUploadService,
  connector,
)

export default enhance(AddAdministrationModalContainer)

